import * as React from "react";

/** Keep only what’s actually used. Add keys later if needed. */
export type LayoutOptions = {
  /** Page header & crumbs are now inside the page body (headerless desktop). */
  hideBreadcrumb?: boolean;
  /** Title shown by your PageHeader (if you use one). */
  pageTitle?: string;
  /** Right-aligned actions in PageHeader. */
  pageActions?: React.ReactNode;
  /** Show a read-only pill in PageHeader. */
  readOnlyBadge?: boolean;
};

type Ctx = {
  options: LayoutOptions;
  setOptions: (
    next:
      | Partial<LayoutOptions>
      | ((prev: LayoutOptions) => Partial<LayoutOptions>),
  ) => void;
};

/** Safe defaults: show breadcrumbs; no title; no actions; no badge. */
const LayoutOptionsContext = React.createContext<Ctx | null>(null);

export function LayoutOptionsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [options, setOptionsState] = React.useState<LayoutOptions>({});

  // Shallow-compare to avoid useless re-renders and effect loops
  const shallowEqual = (a: LayoutOptions, b: LayoutOptions) => {
    const ak = Object.keys(a) as (keyof LayoutOptions)[];
    const bk = Object.keys(b) as (keyof LayoutOptions)[];
    if (ak.length !== bk.length) return false;
    for (const k of ak) {
      if (a[k] !== b[k]) return false;
    }
    return true;
  };

  const setOptions: Ctx["setOptions"] = React.useCallback((next) => {
    setOptionsState((prev) => {
      const patch = typeof next === "function" ? (next as any)(prev) : next;
      const merged = { ...prev, ...patch } as LayoutOptions;
      return shallowEqual(prev, merged) ? prev : merged;
    });
  }, []);

  const value = React.useMemo(() => ({ options, setOptions }), [options, setOptions]);
  return (
    <LayoutOptionsContext.Provider value={value}>
      {children}
    </LayoutOptionsContext.Provider>
  );
}

export function useLayoutOptions(): Ctx {
  const ctx = React.useContext(LayoutOptionsContext);
  if (!ctx)
    throw new Error(
      "useLayoutOptions must be used within <LayoutOptionsProvider>",
    );
  return ctx;
}
